/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { omit, pick } from 'lodash';
import { AppServiceRequestProcessor } from '@services/state-machine';
import { CreateTxnMessage } from '@services/tokenization-service/messages/create-txn.message';
import { VApplication } from '@/application';
import { GraphqlServerModule } from '@vlinder-web/graphql-server-module-react';
import { CREATE_TXN } from '@services/tokenization-service/graphql/createTxn.mutation';

export interface ICreateTransactionArgs {
  batchDetail: {
    item: string;
    batchDate: string;
    batchExpiry?: string;
    batchNo: string;
    [key: string]: any;
  };
  complianceProof: {
    name: string;
    description?: string;
    icon?: string;
    doc: string;
    validFrom: string;
    validUntil?: string;
  }[];
  recipients: {
    id: string;
  }[];
  whoIsMyOrg: string;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync('services.GraphqlServerModule') as GraphqlServerModule;
};

export interface ITransactionServiceMethods extends IPluginMethodMap {
  createTxn(args: ICreateTransactionArgs): Promise<any>;
}

export class TransactionService
  extends AppServiceRequestProcessor
  implements IAgentPlugin
{
  readonly methods: ITransactionServiceMethods;
  constructor() {
    super();
    this.methods = {
      createTxn: this.createTxn.bind(this),
    };
  }

  async createTxn(args: ICreateTransactionArgs) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient('graphql-gateway');
    try {
      if (client) {
        const msg = new CreateTxnMessage({
          raw: args,
        });
        const res = await client.mutate({
          mutation: CREATE_TXN,
          variables: {
            input: [
              {
                batchDate: msg?.raw?.batchDetail?.batchDate,
                item: msg?.raw?.batchDetail?.item,
                batchExpiry: msg?.raw?.batchDetail?.batchExpiry,
                batchNo: msg?.raw?.batchDetail?.batchNo,
                digitalRecord: JSON.stringify(msg?.raw?.batchDetail || {}),
                from: {
                  connect: {
                    where: {
                      node: {
                        id: msg?.raw?.whoIsMyOrg,
                      },
                    },
                  },
                },
                complianceRecord: this.createComplianceProofRec(
                  msg?.raw?.complianceProof
                ),
                namespace: {
                  connect: {
                    where: {
                      node: {
                        id: msg?.raw?.whoIsMyOrg,
                      },
                    },
                  },
                },
                transitionedTo: {
                  create: {
                    node: {
                      batchDate: msg?.raw?.batchDetail?.batchDate,
                      item: msg?.raw?.batchDetail?.item,
                      batchExpiry: msg?.raw?.batchDetail?.batchExpiry,
                      batchNo: msg?.raw?.batchDetail?.batchNo,
                      digitalRecord: JSON.stringify(
                        msg?.raw?.batchDetail || {}
                      ),
                      from: {
                        connect: {
                          where: {
                            node: {
                              id: msg?.raw?.whoIsMyOrg,
                            },
                          },
                        },
                      },
                      complianceRecord: this.createComplianceProofRec(
                        msg?.raw?.complianceProof
                      ),
                      namespace: {
                        connect: {
                          where: {
                            node: {
                              id: msg?.raw?.recipients[0]?.id,
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            ],
          },
          fetchPolicy: 'no-cache',
        });
        if (res?.data) {
        }
      }
    } catch (err: any) {
      throw err;
    }
  }

  private createComplianceProofRec(rec: any[]) {
    const records: any = {
      create: [],
    };
    if (rec && Array.isArray(rec) && rec.length > 0) {
      for (const obj of rec) {
        if (obj) {
          let node: any = {};
          node = Object.assign(node, obj);
          records.create.push({ node: node });
        }
      }
    }
    return records;
  }
}
